// CRM demo — données fictives partagées (sociétés, contacts, transactions).
// Aucune source backend : ce CRM est un affichage de démonstration isolé.
// Tout est croisé par identifiants stables (string) pour permettre le
// cross-linking sociétés ↔ contacts ↔ transactions via react-router.

// ─── Formatage français ───
export const formatEur = (n) =>
  (n ?? 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 }) + ' €';

export const formatEurK = (n) =>
  Math.round((n ?? 0) / 1000).toLocaleString('fr-FR');

export const formatDateFr = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};

// Échéance relative (jours restants) à partir d'une date ISO, base = aujourd'hui démo.
const DEMO_TODAY = new Date('2026-06-20T00:00:00');
export const daysUntil = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.round((d.getTime() - DEMO_TODAY.getTime()) / 86400000);
};

// ─── Étapes du pipeline (ordre = progression commerciale) ───
// `key` sert d'identifiant stable ; `accent` = variable Cosmos pour la pastille.
export const STAGES = [
  { key: 'prospection', title: 'Prospection', accent: 'var(--slate-400)' },
  { key: 'qualification', title: 'Qualification', accent: 'var(--sky-500)' },
  { key: 'proposition', title: 'Proposition', accent: 'var(--iris-500)' },
  { key: 'negociation', title: 'Négociation', accent: 'var(--amber-500)' },
  { key: 'gagne', title: 'Gagné', accent: 'var(--emerald-500)' },
];

export const stageByKey = (key) => STAGES.find((s) => s.key === key) || null;
export const stageIndex = (key) => STAGES.findIndex((s) => s.key === key);

// Mapping étape → tonalité de badge (classes Cosmos sémantiques).
export const STAGE_TONE = {
  prospection: 'neutral',
  qualification: 'info',
  proposition: 'primary',
  negociation: 'warning',
  gagne: 'success',
};

// Mapping statut de contact → libellé + tonalité.
export const CONTACT_STATUS = {
  client: { label: 'Client', tone: 'success' },
  prospect: { label: 'Prospect', tone: 'info' },
  lead: { label: 'Lead', tone: 'neutral' },
};

// ─── Sociétés ───
export const companies = [
  {
    id: 'acme',
    name: 'Acme SA',
    emoji: '🏢',
    status: 'prospect',
    sector: 'Industrie',
    headcount: '50–200',
    website: 'acme.fr',
    owner: 'Marie Dubois',
    clientSince: null,
  },
  {
    id: 'borealis',
    name: 'Borealis',
    emoji: '🧊',
    status: 'prospect',
    sector: 'Énergie',
    headcount: '200–500',
    website: 'borealis.io',
    owner: 'Hugo Moreau',
    clientSince: null,
  },
  {
    id: 'helios',
    name: 'Helios Group',
    emoji: '☀️',
    status: 'prospect',
    sector: 'Conseil',
    headcount: '500–1000',
    website: 'helios.io',
    owner: 'Jean Martin',
    clientSince: null,
  },
  {
    id: 'nimbus',
    name: 'Nimbus SARL',
    emoji: '🌫️',
    status: 'prospect',
    sector: 'Logiciel',
    headcount: '20–50',
    website: 'nimbus.fr',
    owner: 'Sophie Bernard',
    clientSince: null,
  },
  {
    id: 'vega',
    name: 'Vega Tech',
    emoji: '⭐',
    status: 'lead',
    sector: 'Matériel informatique',
    headcount: '10–20',
    website: 'vega.tech',
    owner: 'Pierre Durand',
    clientSince: null,
  },
  {
    id: 'orion',
    name: 'Orion Médias',
    emoji: '🛰️',
    status: 'client',
    sector: 'Médias & édition',
    headcount: '200–500',
    website: 'orion.media',
    owner: 'Marie Dubois',
    clientSince: '2024-03-01',
  },
  {
    id: 'lumen',
    name: 'Lumen Co',
    emoji: '💡',
    status: 'client',
    sector: 'Énergie renouvelable',
    headcount: '100–200',
    website: 'lumen.co',
    owner: 'Jean Martin',
    clientSince: '2025-01-15',
  },
];

export const companyById = (id) => companies.find((c) => c.id === id) || null;

// ─── Contacts ───
export const contacts = [
  {
    id: 'c-rousseau',
    firstName: 'Camille',
    lastName: 'Rousseau',
    role: 'Directrice des achats',
    companyId: 'acme',
    email: 'c.rousseau@acme.fr',
    phone: '+33 1 42 18 09 30',
    status: 'prospect',
    primary: true,
  },
  {
    id: 't-lefevre',
    firstName: 'Thomas',
    lastName: 'Lefèvre',
    role: 'Directeur technique',
    companyId: 'helios',
    email: 't.lefevre@helios.io',
    phone: '+33 4 78 22 11 84',
    status: 'prospect',
    primary: true,
  },
  {
    id: 'i-moreau',
    firstName: 'Inès',
    lastName: 'Moreau',
    role: 'Responsable SI',
    companyId: 'nimbus',
    email: 'i.moreau@nimbus.fr',
    phone: '+33 5 56 44 70 12',
    status: 'prospect',
    primary: true,
  },
  {
    id: 'l-petit',
    firstName: 'Lucas',
    lastName: 'Petit',
    role: 'Président',
    companyId: 'vega',
    email: 'lucas@vega.tech',
    phone: '+33 6 12 90 45 77',
    status: 'lead',
    primary: true,
  },
  {
    id: 's-benali',
    firstName: 'Sarah',
    lastName: 'Benali',
    role: 'Directrice administrative & financière',
    companyId: 'orion',
    email: 's.benali@orion.media',
    phone: '+33 1 84 20 55 12',
    status: 'client',
    primary: true,
  },
  {
    id: 'k-haddad',
    firstName: 'Karim',
    lastName: 'Haddad',
    role: 'Responsable SI',
    companyId: 'orion',
    email: 'k.haddad@orion.media',
    phone: '+33 1 84 20 55 31',
    status: 'client',
    primary: false,
  },
  {
    id: 'a-leroy',
    firstName: 'Aurélie',
    lastName: 'Leroy',
    role: 'Directrice générale',
    companyId: 'lumen',
    email: 'a.leroy@lumen.co',
    phone: '+33 2 40 18 62 09',
    status: 'client',
    primary: true,
  },
  {
    id: 'm-faure',
    firstName: 'Mathieu',
    lastName: 'Faure',
    role: 'Acheteur',
    companyId: 'borealis',
    email: 'm.faure@borealis.io',
    phone: '+33 3 88 14 25 60',
    status: 'prospect',
    primary: true,
  },
];

export const contactById = (id) => contacts.find((c) => c.id === id) || null;
export const contactFullName = (c) => (c ? `${c.firstName} ${c.lastName}` : '');
export const contactsByCompany = (companyId) =>
  contacts.filter((c) => c.companyId === companyId);

// ─── Transactions (deals) ───
export const deals = [
  {
    id: 'd-acme-1',
    name: 'Équipement parc 2026',
    companyId: 'acme',
    contactId: 'c-rousseau',
    amount: 12000,
    stage: 'prospection',
    probability: 20,
    owner: 'Marie Dubois',
    dueDate: '2026-06-23',
    details:
      'Renouvellement du parc de licences Orbit pour les équipes achats et logistique. Premier contact qualifié, périmètre à préciser.',
  },
  {
    id: 'd-borealis-1',
    name: 'Plateforme collaborative',
    companyId: 'borealis',
    contactId: 'm-faure',
    amount: 8500,
    stage: 'prospection',
    probability: 25,
    owner: 'Hugo Moreau',
    dueDate: '2026-06-26',
    details:
      "Déploiement d'un espace collaboratif inter-sites. En attente d'un budget validé côté direction.",
  },
  {
    id: 'd-helios-1',
    name: 'Migration CRM',
    companyId: 'helios',
    contactId: 't-lefevre',
    amount: 24000,
    stage: 'qualification',
    probability: 45,
    owner: 'Jean Martin',
    dueDate: '2026-06-22',
    details:
      "Migration de l'ancien CRM vers Orbit, avec reprise de l'historique et formation des équipes commerciales.",
  },
  {
    id: 'd-nimbus-1',
    name: 'Suite gestion de projet',
    companyId: 'nimbus',
    contactId: 'i-moreau',
    amount: 36000,
    stage: 'proposition',
    probability: 60,
    owner: 'Sophie Bernard',
    dueDate: '2026-06-25',
    details:
      'Mise en place de la suite gestion de projet et automatisation des workflows pour 40 utilisateurs.',
  },
  {
    id: 'd-vega-1',
    name: 'Pilote analytique',
    companyId: 'vega',
    contactId: 'l-petit',
    amount: 15500,
    stage: 'proposition',
    probability: 55,
    owner: 'Pierre Durand',
    dueDate: '2026-06-21',
    details:
      "Projet pilote sur le module analytique, avec une phase d'évaluation de trois mois avant généralisation.",
  },
  {
    id: 'd-orion-1',
    name: 'Licences 2026',
    companyId: 'orion',
    contactId: 's-benali',
    amount: 52000,
    stage: 'negociation',
    probability: 80,
    owner: 'Marie Dubois',
    dueDate: '2026-09-30',
    details:
      "Renouvellement et extension des licences Orbit pour l'ensemble des équipes d'Orion Médias (250 sièges), avec module CRM et automatisation des workflows. Clôture prévue fin T3.",
  },
  {
    id: 'd-orion-2',
    name: 'Support premium',
    companyId: 'orion',
    contactId: 'k-haddad',
    amount: 12000,
    stage: 'gagne',
    probability: 100,
    owner: 'Marie Dubois',
    dueDate: '2026-05-30',
    details:
      "Contrat de support premium 24/7 signé pour accompagner le déploiement des licences d'Orion Médias.",
  },
  {
    id: 'd-lumen-1',
    name: 'Refonte portail client',
    companyId: 'lumen',
    contactId: 'a-leroy',
    amount: 28000,
    stage: 'gagne',
    probability: 100,
    owner: 'Jean Martin',
    dueDate: '2026-04-12',
    details:
      'Refonte du portail client et intégration du module de facturation. Transaction gagnée, déploiement en cours.',
  },
];

export const dealById = (id) => deals.find((d) => d.id === id) || null;
export const dealsByCompany = (companyId) =>
  deals.filter((d) => d.companyId === companyId);
export const dealsByContact = (contactId) =>
  deals.filter((d) => d.contactId === contactId);
export const dealsByStage = (stageKey) =>
  deals.filter((d) => d.stage === stageKey);

// ─── Activités (timeline appels / emails / notes) par société ───
// Affichage uniquement ; aucune persistance.
export const activitiesByCompany = {
  orion: [
    { type: 'call', who: 'Marie Dubois', text: 'Appel de qualification — besoin confirmé pour le T3, budget validé.', time: 'Hier · 16:20' },
    { type: 'email', who: 'Marie Dubois', text: 'Envoi de la proposition commerciale v2 (52 000 €).', time: 'Lun. · 09:05' },
    { type: 'note', who: 'Jean Martin', text: 'Décideur : Sarah Benali (DAF). Concurrent en lice : solution interne.', time: '12 juin' },
  ],
  acme: [
    { type: 'call', who: 'Marie Dubois', text: "Premier échange — cadrage du périmètre et des interlocuteurs.", time: '18 juin · 11:00' },
    { type: 'note', who: 'Marie Dubois', text: 'Décision attendue après le comité achats de juillet.', time: '18 juin' },
  ],
  nimbus: [
    { type: 'email', who: 'Sophie Bernard', text: 'Proposition envoyée pour la suite gestion de projet (36 000 €).', time: '17 juin · 14:30' },
    { type: 'call', who: 'Sophie Bernard', text: 'Présentation de la démo à Inès Moreau — retours positifs.', time: '15 juin · 10:00' },
  ],
  helios: [
    { type: 'call', who: 'Jean Martin', text: 'Atelier de cadrage de la migration CRM avec la DSI.', time: '16 juin · 15:00' },
  ],
  vega: [
    { type: 'note', who: 'Pierre Durand', text: 'Lucas Petit valide le principe du pilote, signature à confirmer.', time: '14 juin' },
  ],
  borealis: [
    { type: 'email', who: 'Hugo Moreau', text: 'Relance sur la plateforme collaborative.', time: '13 juin · 09:40' },
  ],
  lumen: [
    { type: 'note', who: 'Jean Martin', text: 'Transaction portail client gagnée, déploiement planifié.', time: '12 avril' },
    { type: 'email', who: 'Jean Martin', text: 'Confirmation de commande reçue.', time: '12 avril · 08:15' },
  ],
};

// Activités par contact (échanges) — sous-ensemble pour la fiche contact.
export const activitiesByContact = {
  's-benali': [
    { type: 'call', who: 'Marie Dubois', text: "Point d'avancement — validation du périmètre T3.", time: 'Hier · 16:20' },
    { type: 'email', who: 'Marie Dubois', text: 'Relance sur la proposition Licences 2026.', time: 'Lun. · 09:05' },
  ],
  'c-rousseau': [
    { type: 'call', who: 'Marie Dubois', text: "Échange de cadrage sur l'équipement du parc.", time: '18 juin · 11:00' },
  ],
  'i-moreau': [
    { type: 'email', who: 'Sophie Bernard', text: 'Envoi de la proposition pour la suite gestion de projet.', time: '17 juin · 14:30' },
  ],
};
